import {
  Button,
  ButtonProps,
  Flex,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
} from '@everlywell/ui-kit';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';

import SectionContainer from '../components/SectionContainer';
import SectionHeader from '../components/SectionHeader';
import { createSharedButtonProps } from '../utils';

export type ComparisonTableHeader = {
  text?: string | null;
  mobileImage?: ImageType | null;
  desktopImage?: ImageType | null;
  backgroundColor?: string | null;
  cta?: Queries.ContentfulCallToAction001 | null;
  textColor?: string | null;
};

export type ComparisonTableRow = {
  text?: string | null;
  textColor?: string | null;
  iconName?: string | null;
  iconColor?: string | null;
  backgroundColor?: string | null;
};

export type ComparisonTableColumn = {
  header?: ComparisonTableHeader;
  rows?: ComparisonTableRow[];
};

export type ComparisonTableProps = {
  eyebrow?: string | null;
  title?: string | null;
  tableColumns?: ComparisonTableColumn[] | null;
};

/**
 * ComparisonTable component
 * Displays a comparison table with multiple columns and rows.
 * @param {string} [props.eyebrow] Eyebrow text
 * @param {string} [props.title] Title text
 * @param {ComparisonTableColumn[]} [props.tableColumns] Table columns
 * @returns {React.ReactElement} ComparisonTable component
 */
const ComparisonTable = ({
  eyebrow,
  title,
  tableColumns,
}: ComparisonTableProps) => {
  const { handleMdpCtaClick } = useMdpModals();

  const maxRows = tableColumns
    ? Math.max(
        ...tableColumns?.map((column) =>
          column?.rows ? column?.rows?.length : 0,
        ),
      )
    : 0;

  const onCtaClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    cta: Queries.ContentfulCallToAction001 | null | undefined,
  ) => {
    handleMdpCtaClick({
      event,
      ctaUrl: cta?.url || '',
    });
    trackCtaClick(cta);
  };

  const trackCtaClick = (
    cta: Queries.ContentfulCallToAction001 | null | undefined,
  ) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'ComparisonTable',
        ctaText: cta?.label || 'No CTA label',
      },
    });
  };

  const getCtaProps = (cta: Queries.ContentfulCallToAction001): ButtonProps => {
    if (!cta?.url) {
      return {};
    }

    return {
      ...createSharedButtonProps(cta?.url),
    };
  };

  const FIRST_COLUMN_WIDTH = 240;

  // Variable to store the text of the first column to show in the second column on mobile
  let firstColumnText = '';

  return (
    <SectionContainer size={['sm', null, 'xl']}>
      <Flex
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          headerAlignment={['center', null, null, 'left']}
          eyebrowAlignment={['center', null, null, 'left']}
          headingSize={['2xl', null, null, 'xl']}
        />
        <TableContainer
          whiteSpace="wrap"
          maxWidth="880px"
          margin="0 auto"
          borderRadius="xl"
          flex="1"
        >
          <Table sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr>
                {tableColumns?.map((column, columnIndex) => {
                  const isFirstColumn = columnIndex === 0;
                  const isEvenColumn = columnIndex % 2 === 0;
                  // Calculate the width for the rest of the columns
                  const restColumnWidth = `calc((100% - ${FIRST_COLUMN_WIDTH}px) / ${
                    tableColumns.length - 1
                  })`;

                  return (
                    <Th
                      key={columnIndex}
                      backgroundColor={
                        column.header?.backgroundColor || undefined
                      }
                      color={column.header?.textColor || undefined}
                      // Hide the first column on mobile
                      display={
                        isFirstColumn
                          ? ['none', null, null, 'table-cell']
                          : undefined
                      }
                      fontSize={['sm', null, 'md']}
                      borderBottom="2px solid white"
                      borderTopRadius={[!isEvenColumn ? 'xl' : 'none']}
                      width={
                        isFirstColumn
                          ? `${FIRST_COLUMN_WIDTH}px`
                          : restColumnWidth
                      }
                      wordBreak="break-word"
                    >
                      <Flex flexDirection="column" gap="2">
                        {/* Mobile Image */}
                        <Image
                          display={[null, null, null, 'none']}
                          width="72px"
                          height="72px"
                          as={GatsbyImage}
                          image={column.header?.mobileImage?.gatsbyImageData}
                          alt={column.header?.mobileImage?.description || ''}
                        />

                        {/* Desktop Image */}
                        <Image
                          display={['none', null, null, 'block']}
                          width="72px"
                          height="72px"
                          as={GatsbyImage}
                          image={column.header?.desktopImage?.gatsbyImageData}
                          alt={column.header?.desktopImage?.description || ''}
                        />
                        {column.header?.text}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {Array.from({ length: maxRows }).map((_, rowIndex) => (
                <Tr key={rowIndex}>
                  {tableColumns?.map((column, columnIndex) => {
                    const currentRow = column?.rows?.[rowIndex];
                    const isLastRow = rowIndex === maxRows - 1;
                    const isFirstColumn = columnIndex === 0;
                    const isSecondColumn = columnIndex === 1;

                    if (isFirstColumn) {
                      // Variable created outside the map to avoid re-initialization
                      firstColumnText = currentRow?.text || '';
                    }

                    return (
                      <Td
                        key={columnIndex}
                        color={
                          currentRow?.textColor ||
                          column.header?.textColor ||
                          undefined
                        }
                        backgroundColor={
                          currentRow?.backgroundColor ||
                          // Same color as the header
                          column?.header?.backgroundColor ||
                          undefined
                        }
                        // Hide the first column on mobile
                        display={
                          isFirstColumn
                            ? ['none', null, null, 'table-cell']
                            : undefined
                        }
                        borderBottom="2px solid white"
                        fontSize={['sm', null, 'md']}
                        wordBreak="break-word"
                        verticalAlign="bottom"
                      >
                        <Flex flexDirection="column" gap="8">
                          {column?.rows && rowIndex < column?.rows?.length ? (
                            /* Render the row's content */
                            <Flex
                              gap="2"
                              flexDirection={[
                                firstColumnText ? 'column' : 'row',
                                null,
                                null,
                                'row',
                              ]}
                            >
                              {/* The first column is hidden on mobile, so we need to show the text in the second column */}
                              {isSecondColumn && firstColumnText ? (
                                <Text
                                  display={['block', null, null, 'none']}
                                  fontSize="xs"
                                  color="tints.darkGrey"
                                >
                                  {firstColumnText}
                                </Text>
                              ) : null}

                              {currentRow?.iconName ? (
                                <DynamicIcon
                                  icon={currentRow?.iconName as IconKey}
                                  color={currentRow?.iconColor}
                                  weight="fill"
                                />
                              ) : null}

                              {currentRow?.text ? (
                                <Text
                                  fontSize="inherit"
                                  data-testid={`row-text-${columnIndex}-${rowIndex}`}
                                >
                                  {currentRow?.text}
                                </Text>
                              ) : null}
                            </Flex>
                          ) : null}

                          {/* Check if this is the last row and if a CTA is defined in the header */}
                          {isLastRow && column.header?.cta ? (
                            /* Render the CTA on the last row */
                            <Button
                              variant="primary"
                              id={`${column?.header?.cta?.label}-promo`}
                              width={['100%', null, null, 'fit-content']}
                              size="sm"
                              onClick={(event) =>
                                onCtaClick?.(event, column?.header?.cta)
                              }
                              {...getCtaProps(column?.header?.cta)}
                            >
                              {column?.header?.cta?.label}
                            </Button>
                          ) : null}
                        </Flex>
                      </Td>
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
    </SectionContainer>
  );
};

export default ComparisonTable;
